import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as CriticalAssetIcon } from '@elementary/common-ui/assets/16x16/critical-asset-tag-color.svg';
import {
  DaysBackPicker,
  FilterOption,
  FilterPopover,
  Flex,
  FlexCenter,
  FlexColumn,
  SearchField,
  GroupedTable,
} from '@elementary/common-ui/components';
import {
  GroupedData,
  QuickFilter,
  QuickFilters,
  ToggleColumnsMenu,
} from '@elementary/common-ui/components/Table';
import FilterChipRow from '@elementary/common-ui/components/Table/TableFilters/FilterChipRow';
import useDebounce from '@elementary/common-ui/hooks/useDebounce';
import useUrlFilters from '@elementary/common-ui/hooks/useUrlFilters';
import { pluralizeString, sumArr } from '@elementary/common-ui/utils/common';
import { CircularProgress, Tooltip, Typography } from '@mui/material';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import dayjs from 'dayjs';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import useSnackbar from 'shared/hooks/useSnackbar';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import useSearchParams from '@elementary/common-ui/hooks/useSearchParams';
import {
  CHAT_INCIDENT_ID,
  INCIDENT_ID,
} from '@elementary/common-ui/constants/urlParams';
import useViewLoadTracking from '@elementary/common-ui/hooks/useViewLoadTracking';
import {
  DAYS_BACK,
  INCIDENT_LIST_FILTERS,
} from '@elementary/common-ui/constants/urlParams';
import {
  OPEN_INCIDENTS,
  ACKNOWLEDGED_INCIDENTS,
  RESOLVED_INCIDENTS,
  INCIDENTS_OVERVIEW,
  INCIDENT_FILTERS,
} from '@elementary/common-ui/constants/queryKeys';
import useSelectedTreeItem from '@elementary/common-ui/hooks/useSelectedTreeItem';
import useTreeViewParams from '@elementary/common-ui/hooks/useTreeViewParams';
import { hashArray } from '@elementary/common-ui/utils/hashing';
import useAccount from '@elementary/common-ui/hooks/useAccount';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import useChatContext from '@elementary/common-ui/hooks/useChatContext';
import { capitalizeWords } from '@elementary/common-ui/utils/common';
import useIncidentMetadataOptions from '../hooks/useIncidentMetadataOptions';
import { fetchIncidentOverview, fetchIncidents, updateIncidents } from '../api';
import { Incident, IncidentDetails, Severity, Status } from '../types';
import {
  getIncidentDisplayLabel,
  severityGrayscaleIconMap,
  statusIconMap,
} from '../utils';
import useIncidentColumns from '../hooks/useIncidentColumns';
import { useIncidentOverviewSidePanel } from './IncidentOverviewSidePanel';
import IncidentsFloatingActionMenu from './IncidentsFloatingActionMenu';

const Wrapper = styled(FlexColumn)`
  gap: 24px;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const SeverityCount = styled(Flex)`
  gap: 4px;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.common.gray4};
`;

const quickFilters: QuickFilter[] = [
  {
    name: 'Unassigned',
    filters: [
      {
        property: 'assignee',
        operator: 'equals',
        value: [NO_ASSIGNEE_VALUE],
      },
    ],
  },
  {
    name: 'High priority',
    tooltip: 'Unassigned, severity is high or critical',
    filters: [
      {
        property: 'assignee',
        operator: 'equals',
        value: [NO_ASSIGNEE_VALUE],
      },
      {
        property: 'severity',
        operator: 'equals',
        value: ['High', 'Critical'],
      },
    ],
  },
  {
    name: 'Impacting Critical Assets',
    tooltip: 'Incidents impacting critical assets',
    icon: <CriticalAssetIcon />,
    filters: [
      {
        property: 'impacting_critical_assets',
        operator: 'equals',
        value: ['true'],
      },
    ],
  },
];

const commonQueryOptions = {
  refetchInterval: 10 * 60 * 1000, // 10 minutes
  keepPreviousData: true,
};

const IncidentTable = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [excludedItems, setExcludedItems] = useState<string[]>([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const debouncedSearch = useDebounce(search);
  const selectedEnvId = useSelectedEnv();
  const queryClient = useQueryClient();
  const { openSnackbar } = useSnackbar();
  const { urlTreeNode, urlSavedView } = useTreeViewParams();
  const { selectedTreeNode, selectedTreeFolderNodes } = useSelectedTreeItem();
  const { data: account } = useAccount();
  const { openPanel } = useAiChatPanel();

  const URL_FILTERS_KEY = INCIDENT_LIST_FILTERS;

  const { urlFilters, clearAllFilters, addFilter, clearFilter, updateFilter } =
    useUrlFilters({ urlKey: URL_FILTERS_KEY });

  const {
    incidentFilters,
    severityOptions,
    statusOptions,
    assigneeOptions,
    canEdit,
    usersData,
  } = useIncidentMetadataOptions();

  const daysBackParam = searchParams.get(DAYS_BACK);
  const daysBack = daysBackParam ? parseInt(daysBackParam, 10) : 3;
  const selectedDate = dayjs().subtract(daysBack, 'days');

  const { onOpen: openIncidentOverview } = useIncidentOverviewSidePanel();

  const openIncidentsFilter = {
    property: 'status',
    operator: 'equals',
    value: ['Open'],
  };

  const acknowledgedIncidentsFilter = {
    property: 'status',
    operator: 'equals',
    value: ['Acknowledged'],
  };

  const resolvedIncidentsFilter = {
    property: 'status',
    operator: 'equals',
    value: ['Resolved'],
  };

  const resetSelection = () => {
    setIsSelectAll(false);
    setSelectedItems([]);
    setExcludedItems([]);
  };

  const selectedTreeNodeIds = useMemo(() => {
    if (!urlTreeNode && !urlSavedView) {
      return undefined;
    }

    if (selectedTreeFolderNodes?.length) {
      return selectedTreeFolderNodes.map((node) => node.id);
    }

    if (selectedTreeNode) {
      return [selectedTreeNode.id];
    }

    return [];
  }, [selectedTreeFolderNodes, selectedTreeNode, urlSavedView, urlTreeNode]);

  const selectedTreeNodesHash = useMemo(
    () => selectedTreeNodeIds && hashArray(selectedTreeNodeIds),
    [selectedTreeNodeIds],
  );

  const {
    data: openIncidents,
    isFetching: isOpenIncidentsFetching,
    fetchNextPage: fetchNextOpenIncidentsPage,
  } = useInfiniteQuery(
    [
      OPEN_INCIDENTS,
      selectedEnvId,
      urlFilters,
      daysBack,
      debouncedSearch,
      selectedTreeNodesHash,
    ],
    ({ pageParam = 1 }) =>
      fetchIncidents(
        selectedEnvId!,
        pageParam,
        50,
        JSON.stringify([...(urlFilters || []), openIncidentsFilter]),
        daysBack,
        debouncedSearch,
        selectedTreeNodeIds,
      ),
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.page ? firstPage.page - 1 : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.page >= lastPage.pages ? undefined : lastPage.page + 1,
      enabled: !!selectedEnvId,
      ...commonQueryOptions,
    },
  );

  const {
    data: acknowledgedIncidents,
    isFetching: isAcknowledgedIncidentsFetching,
    fetchNextPage: fetchNextAcknowledgedIncidentsPage,
  } = useInfiniteQuery(
    [
      ACKNOWLEDGED_INCIDENTS,
      selectedEnvId,
      urlFilters,
      daysBack,
      debouncedSearch,
      selectedTreeNodesHash,
    ],
    ({ pageParam = 1 }) =>
      fetchIncidents(
        selectedEnvId!,
        pageParam,
        50,
        JSON.stringify([...(urlFilters || []), acknowledgedIncidentsFilter]),
        daysBack,
        debouncedSearch,
        selectedTreeNodeIds,
      ),
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.page ? firstPage.page - 1 : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.page >= lastPage.pages ? undefined : lastPage.page + 1,
      enabled: !!selectedEnvId,
      ...commonQueryOptions,
    },
  );

  const {
    data: resolvedIncidents,
    isFetching: isResolvedIncidentsFetching,
    fetchNextPage: fetchNextResolvedIncidentsPage,
  } = useInfiniteQuery(
    [
      RESOLVED_INCIDENTS,
      selectedEnvId,
      urlFilters,
      daysBack,
      debouncedSearch,
      selectedTreeNodesHash,
    ],
    ({ pageParam = 1 }) =>
      fetchIncidents(
        selectedEnvId!,
        pageParam,
        50,
        JSON.stringify([...(urlFilters || []), resolvedIncidentsFilter]),
        daysBack,
        debouncedSearch,
        selectedTreeNodeIds,
      ),
    {
      getPreviousPageParam: (firstPage) =>
        firstPage.page ? firstPage.page - 1 : undefined,
      getNextPageParam: (lastPage) =>
        lastPage.page >= lastPage.pages ? undefined : lastPage.page + 1,
      enabled: !!selectedEnvId,
      ...commonQueryOptions,
    },
  );

  const { data: incidentsOverview } = useQuery(
    [
      INCIDENTS_OVERVIEW,
      selectedEnvId,
      daysBack,
      urlFilters,
      debouncedSearch,
      selectedTreeNodesHash,
    ],
    () =>
      fetchIncidentOverview(
        selectedEnvId!,
        daysBack,
        JSON.stringify(urlFilters),
        debouncedSearch,
        selectedTreeNodeIds,
      ),
    {
      ...commonQueryOptions,
    },
  );

  const incidentsSummary = useMemo(
    () =>
      incidentsOverview && {
        open: {
          total: sumArr(
            Object.keys(incidentsOverview.severities_per_status.Open).map(
              (severity) =>
                incidentsOverview.severities_per_status.Open[
                  severity as Severity
                ],
            ),
          ),
          ...incidentsOverview?.severities_per_status.Open,
        },
        acknowledged: {
          total: sumArr(
            Object.keys(
              incidentsOverview.severities_per_status.Acknowledged,
            ).map(
              (severity) =>
                incidentsOverview.severities_per_status.Acknowledged[
                  severity as Severity
                ],
            ),
          ),
          ...incidentsOverview?.severities_per_status.Acknowledged,
        },
        resolved: {
          total: sumArr(
            Object.keys(incidentsOverview.severities_per_status.Resolved).map(
              (severity) =>
                incidentsOverview.severities_per_status.Resolved[
                  severity as Severity
                ],
            ),
          ),
          ...incidentsOverview?.severities_per_status.Resolved,
        },
      },
    [incidentsOverview],
  );

  const selectedCount = useMemo(() => {
    const totalItems =
      (openIncidents?.pages?.[0].total || 0) +
      (acknowledgedIncidents?.pages?.[0].total || 0);

    if (isSelectAll) {
      return totalItems - excludedItems.length;
    }
    return selectedItems.length;
  }, [
    acknowledgedIncidents,
    excludedItems.length,
    isSelectAll,
    openIncidents,
    selectedItems.length,
  ]);

  const { mutateAsync: updateIncidentItems } = useMutation(
    async ({
      all,
      exclude,
      include,
      filters,
      assignee,
      status,
      severity,
    }: {
      all?: boolean;
      exclude?: string[];
      include?: string[];
      filters?: string;
      assignee?: string;
      status?: Status;
      severity?: Severity;
    }) => {
      try {
        await updateIncidents(
          selectedEnvId!,
          all,
          exclude,
          include,
          daysBack,
          filters,
          search,
          assignee,
          status,
          severity,
        );
        queryClient.invalidateQueries([OPEN_INCIDENTS]);
        queryClient.invalidateQueries([ACKNOWLEDGED_INCIDENTS]);
        queryClient.invalidateQueries([RESOLVED_INCIDENTS]);
        queryClient.invalidateQueries([INCIDENTS_OVERVIEW]);
        queryClient.invalidateQueries([INCIDENT_FILTERS]);

        const updatedProperty = assignee
          ? 'assignee'
          : status
          ? 'status'
          : severity
          ? 'severity'
          : undefined;

        const assigneeName = assignee
          ? usersData?.find((user) => user.email === assignee)?.name || assignee
          : '';

        const updatedValue = assigneeName || status || severity;

        const plurlizedItemCount = pluralizeString(
          selectedCount || 1,
          'incident',
          'incidents',
        );

        const snackbarMessage =
          updatedProperty === 'assignee' && updatedValue === NO_ASSIGNEE_VALUE
            ? `Cleared ${updatedProperty} on ${plurlizedItemCount}`
            : `Changed ${plurlizedItemCount} ${updatedProperty} to "${updatedValue}"`;

        openSnackbar({ variant: 'success', message: snackbarMessage });
        resetSelection();
      } catch (error) {
        openSnackbar({ variant: 'error', message: 'Incident update failed' });
      }
    },
  );

  const handleUpdateIncidents = (val: Partial<IncidentDetails>) => {
    if (isSelectAll) {
      // Always include a status filter to exclude resolved incidents when using "all"
      const nonResolvedStatusFilter = {
        property: 'status',
        operator: 'equals',
        value: ['Open', 'Acknowledged'],
      };
      const filtersWithStatus = [
        ...(urlFilters || []),
        nonResolvedStatusFilter,
      ];

      updateIncidentItems({
        all: true,
        exclude: excludedItems,
        filters: JSON.stringify(filtersWithStatus),
        ...val,
      });
    } else if (selectedItems.length) {
      updateIncidentItems({
        include: selectedItems,
        ...val,
      });
    }
  };

  const handleSingleIncidentChange = useCallback(
    (id: string, val: Partial<IncidentDetails>) => {
      updateIncidentItems({
        include: [id],
        ...val,
      });
    },
    [updateIncidentItems],
  );

  const filterOptions: FilterOption[] = useMemo(
    () => [
      {
        property: 'severity',
        propertyLabel: 'Severity',
        operators: ['equals', 'notEquals'],
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'severity')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'assignee',
        propertyLabel: 'Assignee',
        options: [
          { value: NO_ASSIGNEE_VALUE, label: 'No Assignee' },
          ...(incidentFilters?.options
            ?.find((f) => f.property === 'assignee')
            ?.values.map((v) => ({ value: v, label: v })) ||
            [] ||
            []),
        ],
      },
      {
        property: 'owner',
        propertyLabel: 'Owner',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'owner')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'tag',
        propertyLabel: 'Tag',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'tag')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'model',
        propertyLabel: 'Model',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'model')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'test_name',
        propertyLabel: 'Test Name',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'test_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'impacting_critical_assets',
        propertyLabel: 'Impacting Critical Assets',
        operators: ['equals', 'notEquals'],
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'impacting_critical_assets')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'database_name',
        propertyLabel: 'Database',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'database_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'schema_name',
        propertyLabel: 'Schema',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'schema_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'table_name',
        propertyLabel: 'Table',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'table_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'column_name',
        propertyLabel: 'Column',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'column_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
      {
        property: 'connector_name',
        propertyLabel: 'Connector',
        options:
          incidentFilters?.options
            ?.find((f) => f.property === 'connector_name')
            ?.values.map((v) => ({ value: v, label: v })) || [],
      },
    ],
    [incidentFilters],
  );

  const allOpenIncidents = useMemo(
    () => openIncidents?.pages.map((page) => page.items).flat() || [],
    [openIncidents?.pages],
  );

  const allAcknowledgedIncidents = useMemo(
    () => acknowledgedIncidents?.pages.map((page) => page.items).flat() || [],
    [acknowledgedIncidents?.pages],
  );

  const allResolvedIncidents = useMemo(
    () => resolvedIncidents?.pages.map((page) => page.items).flat() || [],
    [resolvedIncidents?.pages],
  );

  const allIncidents = useMemo(
    () => [
      ...allOpenIncidents,
      ...allAcknowledgedIncidents,
      ...allResolvedIncidents,
    ],
    [allOpenIncidents, allAcknowledgedIncidents, allResolvedIncidents],
  );

  const selectedIncidents = useMemo(
    () =>
      isSelectAll
        ? allIncidents.filter(
            (inc) =>
              inc.status !== 'Resolved' && !excludedItems.includes(inc.id),
          )
        : selectedItems
            .map((id) => allIncidents.find((inc) => inc.id === id))
            .filter((inc): inc is Incident => !!inc),
    [isSelectAll, excludedItems, selectedItems, allIncidents],
  );

  const selectedIncidentIds = useMemo(
    () => selectedIncidents.map((inc) => inc.id),
    [selectedIncidents],
  );

  const selectedDisplayLabels = useMemo(
    () =>
      selectedIncidents.map((inc) =>
        getIncidentDisplayLabel(
          inc.additional_data.display_name,
          inc.additional_data.sub_type,
        ),
      ),
    [selectedIncidents],
  );

  const chatContext = useMemo(() => {
    if (selectedIncidentIds.length === 0) return undefined;
    return {
      incidentIds: selectedIncidentIds,
      displayLabels: selectedDisplayLabels,
    };
  }, [selectedIncidentIds, selectedDisplayLabels]);

  useChatContext(chatContext);

  useEffect(() => {
    if (
      !isLoaded &&
      !!allOpenIncidents &&
      !!allAcknowledgedIncidents &&
      !!allResolvedIncidents
    ) {
      setIsLoaded(true);
    }
  }, [
    allAcknowledgedIncidents,
    allOpenIncidents,
    allResolvedIncidents,
    isLoaded,
  ]);

  const timeToResolve = incidentsOverview?.average_seconds_to_resolve
    ? capitalizeWords(
        dayjs
          .duration(incidentsOverview.average_seconds_to_resolve, 'seconds')
          .humanize(),
      )
    : '-';

  const renderSeverityMetadata = (
    summary?: Record<Severity | 'total', number>,
  ) => (
    <Flex sx={{ gap: 1.5 }}>
      {(['Low', 'Normal', 'High', 'Critical'] as Severity[])
        .filter((severity) => summary?.[severity])
        .map((severity) => (
          <Tooltip key={severity} title={`${severity} severity`}>
            <SeverityCount>
              {severityGrayscaleIconMap[severity]}
              {summary?.[severity]}
            </SeverityCount>
          </Tooltip>
        ))}
    </Flex>
  );

  const groupedData: GroupedData<Incident>[] = useMemo(
    () => [
      {
        title: 'Open',
        icon: statusIconMap.Open,
        count: incidentsSummary?.open.total,
        metadataRenderer: () => renderSeverityMetadata(incidentsSummary?.open),
        data: allOpenIncidents,
      },
      {
        title: 'Acknowledged',
        icon: statusIconMap.Acknowledged,
        count: incidentsSummary?.acknowledged.total,
        metadataRenderer: () =>
          renderSeverityMetadata(incidentsSummary?.acknowledged),
        data: allAcknowledgedIncidents,
      },
      {
        title: 'Resolved',
        icon: statusIconMap.Resolved,
        count: incidentsSummary?.resolved.total,
        metadataRenderer: () => (
          <Typography sx={{ color: 'common.gray4' }}>
            Avg time to resolve: {timeToResolve}
          </Typography>
        ),
        data: allResolvedIncidents,
      },
    ],
    [
      allOpenIncidents,
      allAcknowledgedIncidents,
      allResolvedIncidents,
      incidentsSummary,
      timeToResolve,
    ],
  );

  const handleGroupScrollToBottom = (groupTitle: string) => {
    if (groupTitle === 'Open') {
      fetchNextOpenIncidentsPage();
    } else if (groupTitle === 'Acknowledged') {
      fetchNextAcknowledgedIncidentsPage();
    } else if (groupTitle === 'Resolved') {
      fetchNextResolvedIncidentsPage();
    }
  };

  const columns = useIncidentColumns({
    severityOptions,
    statusOptions,
    assigneeOptions,
    usersData,
    disabled: !canEdit,
    isAiIncidentsEnabled: account?.ai_features_enabled,
    incidentFilters,
    onChange: handleSingleIncidentChange,
    onOpenAIChat: (id, startMessage, displayLabel) =>
      openPanel({
        startMessage,
        context: { incidentIds: [id], displayLabels: [displayLabel] },
      }),
  });

  const highlightedId =
    searchParams.get(INCIDENT_ID) || searchParams.get(CHAT_INCIDENT_ID);

  useViewLoadTracking({
    name: 'Incident List',
    loadDeps: [
      (allOpenIncidents && !isOpenIncidentsFetching) || null,
      (allAcknowledgedIncidents && !isAcknowledgedIncidentsFetching) || null,
      (allResolvedIncidents && !isResolvedIncidentsFetching) || null,
    ],
    contextId:
      JSON.stringify(urlFilters) +
      daysBackParam +
      debouncedSearch +
      selectedTreeNodesHash,
  });

  const isLoading =
    isOpenIncidentsFetching ||
    isAcknowledgedIncidentsFetching ||
    isResolvedIncidentsFetching;

  return (
    <Wrapper>
      <FlexColumn sx={{ gap: 2 }}>
        <Flex sx={{ justifyContent: 'space-between', gap: 1 }}>
          <Flex sx={{ gap: 2, flexWrap: 'wrap' }}>
            <SearchField
              sx={{ width: '200px' }}
              onChange={(e) => setSearch(e.target.value)}
              value={search}
            />
            <FilterPopover
              filterOptions={filterOptions}
              onSubmit={(val) => {
                const newValue = (val.value || val.prevValue).filter(
                  (v) => v !== 'all',
                );

                if (val.prevValue?.length || val.prevValue?.includes('all')) {
                  updateFilter(
                    val.property,
                    val.operator,
                    val.prevValue.filter((v) => v !== 'all'),
                    newValue,
                  );
                } else {
                  addFilter(val.property, val.operator, newValue);
                }
              }}
              multiselect
            />
            <QuickFilters
              urlParamsPrefix={URL_FILTERS_KEY}
              quickFilters={quickFilters}
            />
          </Flex>
          <Flex sx={{ alignItems: 'flex-start' }}>
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              <ToggleColumnsMenu
                columns={columns}
                localStorageKey="incidents_table"
              />
              <DaysBackPicker
                value={selectedDate}
                onChange={(val) => {
                  const newDaysBack = dayjs().diff(val, 'days');
                  setSearchParams((prev) => {
                    prev.set(DAYS_BACK, String(newDaysBack));
                    return prev;
                  });
                }}
              />
            </Flex>
          </Flex>
        </Flex>
        <FilterChipRow
          filterOptions={filterOptions}
          onSubmit={(val) =>
            updateFilter(
              val.property,
              val.operator,
              val.prevValue!,
              val.value,
              val.index,
            )
          }
          selectedFilters={urlFilters}
          clearFilter={clearFilter}
          onClearAll={clearAllFilters}
        />
      </FlexColumn>

      {!isLoaded ? (
        <FlexCenter sx={{ height: '100%' }}>
          <CircularProgress />
        </FlexCenter>
      ) : (
        <GroupedTable
          data={groupedData}
          columns={columns}
          idKey="id"
          localStorageKey="incidents_table"
          urlParamsPrefix={URL_FILTERS_KEY}
          selectionColumn={(incident) => incident.status !== 'Resolved'}
          selectedItems={selectedItems}
          onSelectItem={setSelectedItems}
          isSelectAll={isSelectAll}
          onSelectAllChange={setIsSelectAll}
          excludedItems={excludedItems}
          onExcludeItem={setExcludedItems}
          onRowClick={(incident) => openIncidentOverview(incident.id)}
          focusedRows={highlightedId ? [highlightedId] : []}
          onGroupScrollToBottom={handleGroupScrollToBottom}
          isLoading={isLoading}
          defaultExpandedGroups
          showEmptyGroups
          isControlled
          bottomScrollPadding={100}
        />
      )}

      <IncidentsFloatingActionMenu
        selectedCount={selectedCount}
        severityOptions={severityOptions}
        assigneeOptions={[
          { label: 'No assignee', value: NO_ASSIGNEE_VALUE },
          ...(assigneeOptions || []),
        ]}
        statusOptions={statusOptions}
        onClose={() => resetSelection()}
        onChange={(val) => handleUpdateIncidents(val)}
      />
    </Wrapper>
  );
};

export default IncidentTable;
